import { inject, Injectable } from '@angular/core';

import { MAX_RECONNECTION_ATTEMPTS, RECONNECT_INTERVAL } from '@rpgc-ai/shared-interfaces/consts/websocket.const';
import type { EWebSocketMessage } from '@rpgc-ai/shared-interfaces/enums/websocket.enum';
import { IS_SERVER } from '@rpgc-ai/shared-interfaces/tokens/is-server.token';

import { environment } from '@rpgc-ai/environments';

type Listener = (data: unknown) => void;

@Injectable({ providedIn: 'root' })
export class WebSocketConnectionManager {
  readonly #isServer = inject(IS_SERVER);

  #socket: WebSocket | null = null;
  #refCount = 0;
  #reconnectAttempts = 0;
  #reconnectTimeout: ReturnType<typeof setTimeout> | null = null;

  readonly #listeners = new Map<EWebSocketMessage, Set<Listener>>();

  connect(): void {
    this.#refCount++;

    if (this.#isServer || this.#socket?.readyState === WebSocket.OPEN) {
      return;
    }

    if (this.#socket?.readyState === WebSocket.CONNECTING || this.#socket?.readyState === WebSocket.CLOSING) {
      return;
    }

    this.#openSocket();
  }

  disconnect(): void {
    this.#refCount = Math.max(0, this.#refCount - 1);

    if (this.#refCount > 0) {
      return;
    }

    this.#clearReconnectTimeout();
    this.#cleanupSocket();
    this.#reconnectAttempts = 0;
  }

  subscribe(eventKey: EWebSocketMessage, listener: Listener): () => void {
    let set = this.#listeners.get(eventKey);
    if (!set) {
      set = new Set();
      this.#listeners.set(eventKey, set);
    }
    set.add(listener);

    return () => {
      set!.delete(listener);
      if (set!.size === 0) {
        this.#listeners.delete(eventKey);
      }
    };
  }

  #openSocket(): void {
    this.#cleanupSocket();

    try {
      this.#socket = new WebSocket(`${environment.SOCKET_URL}/ws`);

      this.#socket.onopen = () => {
        this.#reconnectAttempts = 0;
      };

      this.#socket.onmessage = (event) => {
        this.#handleMessage(event);
      };

      this.#socket.onerror = () => {
        //
      };

      this.#socket.onclose = () => {
        if (this.#refCount > 0) {
          this.#attemptReconnect();
        }
      };
    } catch {
      // Constructor throws only for invalid URLs (SyntaxError) — no recovery possible
    }
  }

  #handleMessage(event: MessageEvent): void {
    try {
      const message = JSON.parse(event.data);
      const listeners = this.#listeners.get(message.event);
      if (listeners) {
        for (const listener of [...listeners]) {
          listener(message);
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error parsing WebSocket message:', error);
    }
  }

  #cleanupSocket(): void {
    if (this.#socket) {
      this.#socket.onopen = null;
      this.#socket.onmessage = null;
      this.#socket.onerror = null;
      this.#socket.onclose = null;
      this.#socket.close();
      this.#socket = null;
    }
  }

  #attemptReconnect(): void {
    if (this.#reconnectAttempts >= MAX_RECONNECTION_ATTEMPTS) {
      return;
    }

    this.#reconnectAttempts++;

    this.#reconnectTimeout = setTimeout(() => {
      this.#reconnectTimeout = null;
      if (this.#refCount > 0) {
        this.#openSocket();
      }
    }, RECONNECT_INTERVAL);
  }

  #clearReconnectTimeout(): void {
    if (this.#reconnectTimeout) {
      clearTimeout(this.#reconnectTimeout);
      this.#reconnectTimeout = null;
    }
  }
}
